import React from 'react';
import ClockPanel from './ClockPanel';
import OverviewPanel from './OverviewPanel';
import DoughnutChart from './DoughnutChart';
import { filledClocks, DAY_LABELS, computeHour } from './defaults';

// Off-screen print-friendly layout used ONLY by the PDF exporter.
//   Page 1  — Cover (logo, title, template name, date, URL)
//   Pages 2+ — One filled clock per page, large chart with hour/minute
//              markers on the right, compact sequence table on the left.
//   Last    — Overview aggregate pie (only when clocks have content).
//
// Each page-worth of content sits inside a <div data-pdf-page="..."> and
// the exporter loops through them in document order. Footers here are
// intentionally minimal — the jsPDF layer draws its own brand footer on
// every page (except the cover), so we don't repeat it in the HTML.

const PAGE_STYLE = {
  width: '1400px',
  minHeight: '900px',
  boxSizing: 'border-box',
  padding: '40px 48px',
  background: '#ffffff',
  color: '#1B2A4A',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

const LOGO_SRC = '/radio-logo.png';

export default function ExportView({ state, templateName }) {
  const filled = filledClocks(state);
  const today = new Date().toLocaleDateString('el-GR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
  const anyContent = filled.length > 0;
  const totalPages = 1 + filled.length + (anyContent ? 1 : 0);

  return (
    <div style={{ position: 'fixed', left: '-99999px', top: 0, background: '#fff' }}>
      {/* Page 1 — cover (brand-heavy, no extra footer overlay) */}
      <div data-pdf-page="cover" style={{ ...PAGE_STYLE, justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ marginTop: '60px' }}>
          <img
            src={LOGO_SRC}
            alt="Radio Solutions"
            style={{ width: '220px', height: '220px', objectFit: 'contain', display: 'block', margin: '0 auto' }}
            crossOrigin="anonymous"
          />
        </div>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '48px', fontWeight: 900, letterSpacing: '2px', color: '#2D0C4B', margin: '0 0 12px' }}>
            RADIO APPS CLOCK BUILDER
          </h1>
          <div style={{ fontSize: '22px', color: '#5B4A7A', fontWeight: 600, marginBottom: '32px' }}>
            {templateName || 'Πρόγραμμα Ραδιοφωνικών Εκπομπών'}
          </div>
          <div style={{ fontSize: '18px', color: '#1B2A4A' }}>
            Ημερομηνία: <b>{today}</b>
          </div>
        </div>
        <div style={{ width: '100%', textAlign: 'center', fontSize: '14px', color: '#7A5EA5' }}>
          Radio Solutions · Radio Apps · www.radio-apps.online
        </div>
      </div>

      {/* Pages 2..N — one clock per page, chart on the right, large */}
      {filled.map((clk, idx) => (
        <div key={clk.index} data-pdf-page={`clock-${idx}`} style={PAGE_STYLE}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: '2px solid #1B2A4A', paddingBottom: '10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img src={LOGO_SRC} alt="" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <div>
                <div style={{ fontSize: '20px', fontWeight: 800, color: '#2D0C4B' }}>
                  RADIO APPS CLOCK BUILDER
                </div>
                {templateName && (
                  <div style={{ fontSize: '13px', color: '#666' }}>{templateName}</div>
                )}
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#666' }}>
              Σελίδα {idx + 2} από {totalPages}
            </div>
          </div>

          <div style={{
            background: '#1B2A4A', color: '#fff',
            padding: '12px 18px', borderRadius: '8px',
            fontSize: '20px', fontWeight: 700, letterSpacing: '1.5px',
          }}>
            {DAY_LABELS[clk.day]} · ΩΡΑ {clk.hourInDay}
          </div>

          <div style={{ flex: 1, display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 54%', minWidth: 0, fontSize: '13px' }}>
              <ClockPanel
                title=""
                rows={clk.hour.rows}
                categories={state.categories}
                speechTypes={state.speech_types}
                onChange={() => {}}
                readOnly
                chartSize={0 /* rendered separately on the right */}
              />
            </div>
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: '580px',
            }}>
              <PdfClockChart
                rows={clk.hour.rows}
                categories={state.categories}
                speechTypes={state.speech_types}
                size={560}
              />
            </div>
          </div>
        </div>
      ))}

      {/* Last page — overview stats */}
      {anyContent && (
        <div data-pdf-page="overview" style={PAGE_STYLE}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: '2px solid #1B2A4A', paddingBottom: '10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img src={LOGO_SRC} alt="" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <div style={{ fontSize: '20px', fontWeight: 800, color: '#2D0C4B' }}>
                Συνολικά Στατιστικά
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#666' }}>
              Σελίδα {totalPages} από {totalPages}
            </div>
          </div>
          <OverviewPanel state={state} />
        </div>
      )}

      {!anyContent && (
        <div data-pdf-page="empty" style={{ ...PAGE_STYLE, justifyContent: 'center', alignItems: 'center' }}>
          <p style={{ fontSize: '18px', color: '#666' }}>
            Δεν έχει συμπληρωθεί κανένα ρολόι ακόμα.
          </p>
        </div>
      )}
    </div>
  );
}

// Standalone big doughnut used ONLY for the PDF (bigger than the UI one,
// with clock face markers). Kept as a tiny local component so the
// ExportView stays self-contained.
function PdfClockChart({ rows, categories, speechTypes, size }) {
  const { segments } = computeHour(rows, categories, speechTypes);
  return <DoughnutChart segments={segments} size={size} showMarkers />;
}
